import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './profile.module.css';
import { logout, login as loginAction } from '../../redux/slice/authSlice';
import { Tabs, Typography } from 'glintly-ui';
import { useUserOrders, useUpdateUser } from '../../api';
import OrdersDetails from './OrdersDetails';
import ProfileDetails from './ProfileDetails';
import KYCDetails from './KYCDetails';
import { toast } from 'react-toastify';
import { openAuthModal } from '../../utils/openAuthModal';

const ProfilePage = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    secMobile: user?.secMobile || '',
    profileImg: user?.profileImg || '',
    address: {
      fullAddress: user?.address?.fullAddress || '',
      landmark: user?.address?.landmark || '',
      city: user?.address?.city || '',
      postalCode: user?.address?.postalCode || '',
      country: user?.address?.country || 'India',
    },
  });

  const {
    data: orders = [],
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
  } = useUserOrders(user?._id);

  const updateUserMutation = useUpdateUser();

  // Calculate user stats
  const totalOrders = orders.length;
  const totalSpent = orders.reduce(
    (sum, order) => sum + (parseInt(order.paymentDetails?.totalAmountWithGst) || 0),
    0
  );
  const completedOrders = orders.filter(
    (order) => order.orderStatus === 'delivered' || order.orderStatus === 'completed'
  ).length;

  if (!user) {
    return (
      <div className={styles.notLoggedIn}>
        <div className={styles.notLoggedInContent}>
          <div className={styles.notLoggedInIcon}>👤</div>
          <h2>You are not logged in</h2>
          <p>Please log in to view your profile</p>
          <button className={styles.loginBtn} type="button" onClick={() => openAuthModal('signin')}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Clear Redux state
    dispatch(logout());
    // Navigate to home
    navigate('/');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setEditForm((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = () => {
    if (!user?._id) {
      toast.error('User ID not found');
      return;
    }

    updateUserMutation.mutate(
      {
        userId: user._id,
        userData: editForm,
      },
      {
        onSuccess: (data) => {
          toast.success('Profile updated successfully!');
          setIsEditing(false);
          // Update Redux state with new user data
          dispatch(loginAction({ user: data.user, token: user.token }));
          localStorage.setItem('user', JSON.stringify(data.user));
        },
        onError: (error) => {
          toast.error(error.response?.data?.error || 'Failed to update profile');
        },
      }
    );
  };

  const handleCancel = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      secMobile: user?.secMobile || '',
      profileImg: user?.profileImg || '',
      address: {
        fullAddress: user?.address?.fullAddress || '',
        landmark: user?.address?.landmark || '',
        city: user?.address?.city || '',
        postalCode: user?.address?.postalCode || '',
        country: user?.address?.country || 'India',
      },
    });
    setIsEditing(false);
  };

  const tabs = [
    { name: 'Personal Info', icon: { left: 'lib-user' } },
    { name: 'KYC Details', icon: { left: 'lib-shield' } },
    { name: 'My Orders', icon: { left: 'lib-cart' } },
    { name: 'Settings', icon: { left: 'lib-settings' } },
  ];

  return (
    <div className={styles.profileContainer}>
      {/* Header */}
      <div className={styles.profileHeader}>
        <div className={styles.profileHero}>
          <div className={styles.avatarContainer}>
            <img
              src={
                user.profileImg ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  user.name || user.email || 'U'
                )}&background=007bff&color=fff&size=120`
              }
              alt="User Avatar"
              className={styles.avatar}
            />
            <div className={styles.avatarOverlay}>
              <span className={styles.editAvatarIcon}>📷</span>
            </div>
          </div>
          <div className={styles.userInfo}>
            <h1 className={styles.userName}>
              {user.name
                ? typeof user.name === 'object'
                  ? user.name.name || user.name.value || JSON.stringify(user.name)
                  : user.name
                : user.name
                  ? typeof user.name === 'object'
                    ? user.name.name || user.name.value || JSON.stringify(user.name)
                    : user.name
                  : user.email
                    ? typeof user.email === 'object'
                      ? user.email.name || user.email.value || JSON.stringify(user.email)
                      : user.email
                    : 'User'}
            </h1>
            <p className={styles.userRole}>
              {user.role
                ? typeof user.role === 'object'
                  ? user.role.name || user.role.value || JSON.stringify(user.role)
                  : user.role
                : 'Customer'}
            </p>
            <div className={styles.userStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{totalOrders}</span>
                <span className={styles.statLabel}>Orders</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>₹{totalSpent.toLocaleString()}</span>
                <span className={styles.statLabel}>Spent</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{completedOrders}</span>
                <span className={styles.statLabel}>Completed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabNavigation}>
        <Tabs
          variant="button"
          options={tabs}
          defaultTab={activeTab}
          onClick={(tabName) => setActiveTab(tabName)}
        />
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'Personal Info' && (
          <ProfileDetails
            user={user}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editForm={editForm}
            handleEditChange={handleEditChange}
            handleSave={handleSave}
            handleCancel={handleCancel}
          />
        )}

        {activeTab === 'KYC Details' && <KYCDetails user={user} />}

        {activeTab === 'My Orders' && (
          <div>
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h5" style={{ color: '#1e293b' }}>
                My Orders
              </Typography>
              <button
                onClick={() => {
                  console.log('Manual refresh clicked');
                  refetchOrders();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Refresh Orders
              </button>
            </div>
            <OrdersDetails
              fetchUserOrders={refetchOrders}
              ordersLoading={ordersLoading}
              ordersError={ordersError}
              orders={orders}
              user={user}
            />
          </div>
        )}

        {activeTab === 'Settings' && (
          <div className={styles.settingsSection}>
            <h2>Account Settings</h2>
            <div className={styles.settingsCard}>
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <h3>Notifications</h3>
                  <p>Manage your notification preferences</p>
                </div>
                <button className={styles.settingToggle}>Toggle</button>
              </div>
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <h3>Privacy</h3>
                  <p>Control your privacy settings</p>
                </div>
                <button className={styles.settingToggle}>Toggle</button>
              </div>
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <h3>Security</h3>
                  <p>Change password and security settings</p>
                </div>
                <button className={styles.settingToggle}>Manage</button>
              </div>
            </div>

            <div className={styles.dangerZone}>
              <h3>Danger Zone</h3>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
